import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import SearchBar from "../../components/SearchBar";
import AddGroup from "../../Modals/addGroup";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { isValidInput } from "../../utils/validations";
import {
  useLazyDeleteMemberGroupQuery,
  useLazyGetMemberGroupsQuery,
} from "../../service/user";
import { showError, showToast } from "../../constants/toasts";
import dayjs from "dayjs";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import Loader from "../../constants/Loader";
import { Group } from "../../types/General";
import { formatDubaiDateTime } from "../../utils/dateTime";

// Type for table columns
interface TableColumn {
  id: keyof Group | "srNo" | "action";
  label: string;
  align?: "left" | "right" | "center";
  minWidth?: number;
  format?: (value: any) => string;
}

// Define the columns with their types
const columns: TableColumn[] = [
  {
    id: "srNo",
    label: "Sr.no",
    align: "center",
    minWidth: 70,
  },
  {
    id: "groupName",
    label: "Title",
    align: "left",
    minWidth: 200,
  },
  {
    id: "memberCount",
    label: "No. of Members",
    align: "center",
    minWidth: 150,
  },
  {
    id: "createdAt",
    label: "Date",
    align: "center",
    minWidth: 120,
    format: (value: string) => formatDubaiDateTime(value),
  },
  {
    id: "action",
    label: "Action",
    align: "center",
    minWidth: 100,
  },
];

const Groups = () => {
  const navigate = useNavigate();
  const [getGroups] = useLazyGetMemberGroupsQuery();
  const [deleteGroup] = useLazyDeleteMemberGroupQuery();
  const [isEdit, setIsEdit] = useState(false);
  const handleCloseModal = () => {
    setOpenModal2(false);
  };
  const [openModal2, setOpenModal2] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [page, setPage] = useState(1);
  const [groupData, setGroupData] = useState<Group[]>([]);
  const [count, setCount] = useState(10);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const getGroupApi = async () => {
    setLoading(true);
    try {
      const response = await getGroups({
        page,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setGroupData(response?.data || []);
        // setCount(response?.data?.totalCount);
      } else {
        setGroupData([]);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const deleteGroupApi = async () => {
    try {
      const response = await deleteGroup({ id: selectedId }).unwrap();
      if (response.status === 200) {
        getGroupApi();
        showToast(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getGroupApi();
  }, [debouncedSearchTerm, page]);

  return (
    <>
      <MainContainer>
        <Loader isLoad={loading} />
        <div className="main_title">
          <h1>Manage Groups</h1>
        </div>
        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                placeholder="Search...."
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </div>
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setIsEdit(false);
                  setOpenModal2(true);
                }}
              >
                Add Group
              </Button>
            </div>
          </div>
          <TableContainer className="table_container">
            <Box className="heading"></Box>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupData?.length ? (
                  groupData?.map((row: Group, i: number) => (
                    <TableRow key={i}>
                      {columns.map((column) => {
                        const value =
                          column.id === "srNo"
                            ? (page - 1) * 10 + i + 1
                            : column.id === "action"
                              ? null
                              : row[column.id as keyof Group];

                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.id === "srNo" && value}
                            {column.id === "groupName" && (
                              <p
                                className="cursor_p"
                                onClick={() =>
                                  navigate(
                                    `/manage-cms/viewGroup/${row?.groupId}`,{state:{title:row?.groupName}}
                                  )
                                }
                              >
                                {value || "-"}
                              </p>
                            )}
                            {column.id === "memberCount" && (value || "-")}
                            {column.id === "createdAt" &&
                              column.format &&
                              column.format(value as string)}
                            {column.id === "action" && (
                              <div className="table_actions v2">
                                <IconButton
                                  onClick={() => {
                                    setOpenDelete(true);
                                    setSelectedId(row?.groupId);
                                  }}
                                >
                                  <img
                                    src="/static/images/trash2_icon.svg"
                                    alt=""
                                  />
                                </IconButton>
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : hasFetched ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No Data Found
                    </td>
                  </tr>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </MainContainer>

      <ConfirmationModal
        text={"Are you sure to delete this Group?"}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={deleteGroupApi}
      />

      <AddGroup
        getGroupApi={getGroupApi}
        open={openModal2}
        onClose={handleCloseModal}
        setOpen={setOpenModal2}
      />
    </>
  );
};
export default Groups;
